import type { Citation } from '@/types'
import { visitorId } from '@/auth/identity'
import type { AudioAnalysis } from '@/types/audio'

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'

function authHeaders(): Record<string,string> {
  const token = sessionStorage.getItem('accessToken')?.trim()
  return token ? { Authorization: `Bearer ${token}` } : { 'X-Aoboshi-Visitor': visitorId() }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: init?.body instanceof FormData ? { ...authHeaders(), ...init.headers } : { 'Content-Type': 'application/json', ...authHeaders(), ...init?.headers },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
    throw new Error(body.error || `HTTP ${response.status}`)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

interface StreamHandlers {
  onMeta?: (data: { sessionId: string; citations: Citation[] }) => void
  onToken: (token: string) => void
  onDone?: (data: unknown) => void
}

export interface VoiceHandlers extends StreamHandlers {
  onError?: (message: string) => void
}

export function parseSSEBlock(block: string): { event: string; data: unknown } | null {
  let event = 'message'; let data = ''
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    if (line.startsWith('data:')) data += line.slice(5).trim()
  }
  if (!data) return null
  return { event, data: JSON.parse(data) }
}

export async function streamChat(payload: Record<string, unknown>, handlers: StreamHandlers, signal?: AbortSignal) {
  const response = await fetch(`${API_BASE}/chat/stream`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(payload), signal,
  })
  if (!response.ok || !response.body) {
    const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
    throw new Error(body.error || '无法建立流式连接')
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const dispatch = (block: string) => {
    const parsed = parseSSEBlock(block)
    if (!parsed) return
    const data = parsed.data as { token?: string; message?: string; sessionId?: string; citations?: Citation[] }
    if (parsed.event === 'meta') handlers.onMeta?.(data as { sessionId: string; citations: Citation[] })
    if (parsed.event === 'token') handlers.onToken(data.token || '')
    if (parsed.event === 'done') handlers.onDone?.(data)
    if (parsed.event === 'error') throw new Error(data.message || '流式请求失败')
  }
  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value, { stream: !done })
    const blocks = buffer.split('\n\n'); buffer = blocks.pop() || ''
    blocks.forEach(dispatch)
    if (done) break
  }
  if (buffer.trim()) dispatch(buffer)
}

export async function synthesizeSpeech(text: string, studentId: string, signal?:AbortSignal): Promise<Blob> {
  const response = await fetch(`${API_BASE}/voice/tts`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ text, studentId }), signal,
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
    throw new Error(body.error || '豆包语音合成失败')
  }
  return response.blob()
}

export function streamVoice(payload: Record<string, unknown>, handlers: VoiceHandlers): { socket: WebSocket; completed: Promise<void> } {
  const target = new URL(`${API_BASE.replace(/\/$/, '')}/voice/ws`, window.location.href)
  target.protocol = target.protocol === 'https:' ? 'wss:' : 'ws:'
  const token = sessionStorage.getItem('accessToken')?.trim()
  const socket = token ? new WebSocket(target, ['bearer', token]) : new WebSocket(target, ['visitor', visitorId()])
  const completed = new Promise<void>((resolve, reject) => {
    socket.onopen = () => socket.send(JSON.stringify({ type: 'transcript', ...payload }))
    socket.onmessage = event => {
      const data = JSON.parse(event.data) as { type: string; token?: string; text?: string; message?: string; sessionId?: string; citations?: Citation[] }
      if (data.type === 'meta') handlers.onMeta?.({ sessionId: data.sessionId || '', citations: data.citations || [] })
      if (data.type === 'token') handlers.onToken(data.token || '')
      if (data.type === 'done') { handlers.onDone?.(data); socket.close(1000); resolve() }
      if (data.type === 'error') { handlers.onError?.(data.message || '语音会话失败'); socket.close(); reject(new Error(data.message || '语音会话失败')) }
    }
    socket.onerror = () => reject(new Error('无法建立语音会话'))
    socket.onclose = event => { if (!event.wasClean && event.code !== 1000) reject(new Error('语音会话意外中断')) }
  })
  return { socket, completed }
}

export async function uploadGrades(file: File) {
  const body = new FormData(); body.append('file', file)
  return api<import('@/types').GradeReport>('/grades/import', { method: 'POST', body })
}

export async function uploadResource(courseId: string, file: File, title = '', status = 'draft') {
  const body = new FormData(); body.append('file', file); body.append('title', title); body.append('status', status)
  return api<import('@/types').Resource>(`/courses/${courseId}/resources/upload`, { method: 'POST', body })
}

export async function analyzeAudio(file: File, selection?: { start: number; end: number }, signal?: AbortSignal, options?: import('@/types/audio').AudioOptions): Promise<AudioAnalysis> {
  const body = new FormData()
  body.append('file', file)
  if (selection) {
    body.append('startSeconds', String(selection.start))
    body.append('endSeconds', String(selection.end))
  }
  for(const [key,value] of Object.entries(options||{}))if(value!==undefined)body.append(key,String(value))
  return api<AudioAnalysis>('/audio/analyze', { method: 'POST', body, signal })
}

export async function extractChatAttachment(file: File, signal?: AbortSignal): Promise<import('@/types/attachments').DocumentExtraction> {
  const body = new FormData()
  body.append('file', file)
  return api<import('@/types/attachments').DocumentExtraction>('/chat/attachments/extract', { method: 'POST', body, signal })
}

export async function exportAudioSelection(file:File, selection:{start:number;end:number}, options:import('@/types/audio').AudioOptions, signal?:AbortSignal):Promise<Blob>{
 const body=new FormData();body.append('file',file);body.append('startSeconds',String(selection.start));body.append('endSeconds',String(selection.end))
 for(const [key,value] of Object.entries(options))if(value!==undefined)body.append(key,String(value))
 const response=await fetch(`${API_BASE}/audio/analyze?format=wav`,{method:'POST',headers:authHeaders(),body,signal})
 if(!response.ok){const data=await response.json().catch(()=>({error:'导出失败'}));throw new Error(data.error||'导出失败')}
 return response.blob()
}

export async function recognizeSpeech(file:File,signal?:AbortSignal):Promise<string>{
 const body=new FormData();body.append('file',file)
 const result=await api<{text:string}>('/voice/asr',{method:'POST',body,signal});return result.text
}
